import { clsx } from 'clsx';
import { Link } from 'react-router';
import { shellCopy } from '@/copy/shell';
import { haptic } from '@/lib/haptics';
import { useUpdateDot } from '@/lib/update';
import { Icon } from './kit';
import type { IconName, TabId } from './module';

/**
 * Bottom tab bar: Home, Stats, Play (raised center ball), Crew, Me. White bar with an ink rule,
 * the active tab gets a lavender fill and a butter underline plate. Pads the home indicator.
 */
const TABS: { id: TabId; to: string; icon: IconName }[] = [
  { id: 'home', to: '/', icon: 'home' },
  { id: 'stats', to: '/stats', icon: 'stats' },
  { id: 'play', to: '/play', icon: 'ball' },
  { id: 'crew', to: '/crew', icon: 'crew' },
  { id: 'me', to: '/me', icon: 'me' },
];

/** Which tab a path belongs to when its route did not say. */
export function tabForPath(pathname: string): TabId {
  if (/^\/(stats)(\/|$)/.test(pathname)) return 'stats';
  if (/^\/(play|game|combine|tournaments|runs|lab)(\/|$)/.test(pathname)) return 'play';
  if (/^\/(crew|p)(\/|$)/.test(pathname)) return 'crew';
  if (/^\/(me|settings|admin|inbox)(\/|$)/.test(pathname)) return 'me';
  return 'home';
}

export function TabBar({ active }: { active: TabId }) {
  const updateDot = useUpdateDot();
  return (
    <nav
      aria-label="Main"
      className="tts-pb-safe tts-no-select fixed inset-x-0 bottom-0 z-(--z-nav) border-t-3 border-ink bg-white"
    >
      <ul className="tts-px-safe mx-auto grid h-(--tts-tabbar-h) max-w-2xl grid-cols-5 items-end">
        {TABS.map((t) => {
          const on = t.id === active;
          const label = shellCopy.tabs[t.id];
          if (t.id === 'play') {
            return (
              <li key={t.id} className="flex justify-center">
                <Link
                  to={t.to}
                  aria-label={label}
                  aria-current={on ? 'page' : undefined}
                  onClick={() => haptic('tap')}
                  className="group -mt-7 mb-1.5 flex flex-col items-center gap-1.5"
                >
                  <span
                    className={clsx(
                      'grid size-[3.75rem] place-items-center rounded-full border-4 border-ink shadow-[4px_4px_0_0_var(--color-ink)] transition-transform duration-(--dur-instant)',
                      'group-active:translate-x-1 group-active:translate-y-1 group-active:shadow-none',
                      on ? 'bg-peach' : 'bg-butter',
                    )}
                  >
                    <Icon
                      name="ball"
                      size={36}
                      strokeWidth={2.6}
                      fill={on ? 'var(--color-white)' : 'var(--color-peach)'}
                    />
                  </span>
                  <span className="tts-display text-[0.8rem] leading-none">{label}</span>
                </Link>
              </li>
            );
          }
          return (
            <li key={t.id} className="flex justify-center">
              <Link
                to={t.to}
                aria-current={on ? 'page' : undefined}
                onClick={() => haptic('tap')}
                className="flex min-h-14 min-w-14 flex-col items-center justify-center gap-1 pb-1.5"
              >
                <span className="relative">
                  <Icon name={t.icon} size={26} fill={on ? 'var(--color-lavender)' : 'var(--color-white)'} />
                  {t.id === 'me' && updateDot && (
                    <span
                      role="img"
                      aria-label={shellCopy.update.dot}
                      data-testid="update-dot"
                      className="absolute -top-1 -right-1.5 size-3.5 rounded-full border-2 border-ink bg-peach"
                    />
                  )}
                </span>
                <span
                  className={clsx(
                    'tts-display px-1.5 text-[0.8rem] leading-tight',
                    on && 'border-2 border-ink bg-butter -skew-x-6',
                  )}
                >
                  {label}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
